import { getUploadedIdentifierForRow } from './identifierDisplay';

export const SECURITY_IDENTITY_FIELD_OPTIONS = Object.freeze([
    Object.freeze({ value: 'company_name', label: 'Company Name' }),
    Object.freeze({ value: 'ticker', label: 'Ticker' }),
    Object.freeze({ value: 'submitted_identifier', label: 'Submitted Identifier' }),
]);

// Internal identifiers can arrive in legacy ticker/name fields. They are keys,
// never customer-facing labels, including in tooltips and accessible names.
export function customerSecurityText(value, row = {}) {
    const text = String(value ?? '').trim();
    return /^cog[_-]/i.test(text)
        || (row?.cognive_id && text.toLowerCase() === String(row.cognive_id).toLowerCase())
        ? '' : text;
}

export function securityTickerPresentation(row) {
    const resolvedTicker = customerSecurityText(row?.msi_listing_label?.ticker, row);
    const uploadedTicker = customerSecurityText(row?.uploaded_ticker, row);
    const sourceTicker = customerSecurityText(row?.ticker, row);

    if (resolvedTicker && uploadedTicker) {
        return {
            value: `${resolvedTicker} (${uploadedTicker})`,
            tooltip: 'The resolved ticker appears first; the ticker from your upload is shown in parentheses.',
        };
    }
    if (resolvedTicker) return { value: resolvedTicker, tooltip: '' };
    if (uploadedTicker) {
        return {
            value: `— (${uploadedTicker})`,
            tooltip: 'A resolved ticker is not available yet; the ticker from your upload is shown in parentheses.',
        };
    }
    return { value: sourceTicker || '—', tooltip: '' };
}

export function securityCompanyName(row) {
    return [row?.msi_listing_label?.security_name, row?.company_name,
        row?.yf_longname, row?.yf_shortname].map((value) => customerSecurityText(value, row))
        .find(Boolean) || '—';
}

export function securitySubmittedIdentifier(row, { includeTickerType = false } = {}) {
    const identifier = getUploadedIdentifierForRow(row);
    if (!customerSecurityText(identifier.value, row)) return '—';
    if (!includeTickerType && identifier.type === 'TICKER') return identifier.value;
    return identifier.type ? `${identifier.type} ${identifier.value}` : identifier.value;
}

export function securityIdentityPresentation(row, field) {
    if (field === 'ticker') return securityTickerPresentation(row);
    if (field === 'submitted_identifier') {
        return { value: securitySubmittedIdentifier(row), tooltip: '' };
    }
    return { value: securityCompanyName(row), tooltip: '' };
}

export function securityCountry(row) {
    return String(
        row?.country
        || row?.msi_listing_label?.country_code
        || row?.requested_country
        || '',
    ).trim() || '—';
}

// The resolved identity columns of a download. A submitted identifier whose
// type is one of these is named "submitted_<type>" instead.
const EXPORT_IDENTITY_COLUMNS = new Set(['company_name', 'ticker', 'country']);

/**
 * A security's identity columns in a download: company_name, ticker, the
 * submitted identifier under its lower-case type (e.g. "sedol") and country,
 * with null for a value that is not available. `submittedColumn` names the
 * submitted identifier's column, or is null when nothing was submitted.
 */
export function securityExportIdentity(row) {
    const identifier = getUploadedIdentifierForRow(row);
    const submittedValue = customerSecurityText(identifier.value, row);
    const type = identifier.type.toLowerCase();
    let submittedColumn = null;
    if (submittedValue) {
        submittedColumn = EXPORT_IDENTITY_COLUMNS.has(type) || !type
            ? `submitted_${type || 'identifier'}`
            : type;
    }
    const companyName = securityCompanyName(row);
    const country = securityCountry(row);
    const ticker = [row?.msi_listing_label?.ticker, row?.ticker, row?.uploaded_ticker]
        .map((value) => customerSecurityText(value, row)).find(Boolean);
    return {
        submittedColumn,
        columns: {
            company_name: companyName === '—' ? null : companyName,
            ticker: ticker || null,
            ...(submittedColumn ? { [submittedColumn]: submittedValue } : {}),
            country: country === '—' ? null : country,
        },
    };
}

export function securityListingMarket(row) {
    const label = row?.msi_listing_label || {};
    const exchange = String(label.exchange_display_name || '').trim();
    const mic = String(label.operating_mic || label.segment_mic || '').trim();
    if (exchange && mic) return `${exchange} (${mic})`;
    return exchange || mic || '—';
}

export function securityIdentityDetails(row) {
    return [
        { label: 'Company', value: securityCompanyName(row) },
        { label: 'Ticker', value: securityTickerPresentation(row).value },
        {
            label: 'Submitted identifier',
            value: securitySubmittedIdentifier(row, { includeTickerType: true }),
        },
        { label: 'Country', value: securityCountry(row) },
        { label: 'Listing market', value: securityListingMarket(row) },
    ];
}
